import { useEffect, useMemo, useState } from "react";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { FaArrowRight, FaEnvelope, FaTiktok } from "react-icons/fa6";
import { fetchPageContent } from "../api/content";
import { fetchMediaKitStats } from "../api/mediaKit";
import { defaultMediaKitContent } from "../content/defaultContent";
import {
  PLATFORM_LABELS,
  PLATFORM_ORDER,
  buildMediaKitSummary,
  fallbackMediaKitStats,
  formatCompactNumber,
  formatPercent,
  formatUnitedStatesRange,
  formatUpdatedDate,
} from "../mediaKit/model";
import MediaKitDownloadButton from "./MediaKitDownloadButton";
import "./css/MediaKit.css";

const platformMeta = {
  youtube: { Icon: FaYoutube, href: "https://www.youtube.com/channel/UCDPPfrCYMXon-KCSRfKymTQ" },
  instagram: { Icon: FaInstagram, href: "https://www.instagram.com/kenandbryn/" },
  tiktok: { Icon: FaTiktok, href: "https://www.tiktok.com/@kenandbryn" },
  facebook: { Icon: FaFacebookF, href: "https://www.facebook.com/kenandbryn/" },
};

export default function MediaKit() {
  const [content, setContent] = useState(defaultMediaKitContent);
  const [stats, setStats] = useState(fallbackMediaKitStats);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.allSettled([fetchPageContent("mediakit"), fetchMediaKitStats()])
      .then(([contentResult, statsResult]) => {
        if (!active) return;
        if (contentResult.status === "fulfilled") {
          setContent({ ...defaultMediaKitContent, ...contentResult.value });
        }
        if (statsResult.status === "fulfilled" && statsResult.value.length) {
          setStats(statsResult.value);
        } else {
          setUsingFallback(true);
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const orderedStats = useMemo(() => PLATFORM_ORDER
    .map((platform) => stats.find((stat) => stat.platform === platform))
    .filter(Boolean), [stats]);
  const summary = useMemo(() => buildMediaKitSummary(orderedStats), [orderedStats]);
  const maxViews = Math.max(...orderedStats.map((stat) => stat.views28d || 0), 1);
  const discoveryStats = orderedStats.filter((stat) => Number.isFinite(stat.discoveryPercent));
  const supportingGeography = summary.supportingCountries.length
    ? summary.supportingCountries.map((country) => country.name).join(", ")
    : "Germany, the United Kingdom, and Canada";

  return (
    <main className="media-kit-page">
      <section className="media-kit-hero media-kit-shell">
        <div className="media-kit-hero-copy">
          <p className="media-kit-kicker">{content.kicker}</p>
          <h1>{content.title}</h1>
          <p className="media-kit-lede">{content.intro}</p>
          <div className="media-kit-actions">
            <MediaKitDownloadButton content={content} stats={orderedStats} summary={summary} />
            <a className="media-kit-secondary-action" href={`mailto:${content.contactEmail}`}>
              <FaEnvelope aria-hidden="true" /> Start a conversation
            </a>
          </div>
        </div>
        <figure className="media-kit-hero-image">
          <img src={content.heroImageUrl} alt={content.heroImageAlt} />
        </figure>
      </section>

      <section className="media-kit-shell media-kit-overview" aria-labelledby="media-kit-scale-title">
        <div className="media-kit-section-heading">
          <p className="media-kit-kicker">Audience scale</p>
          <h2 id="media-kit-scale-title">A multi-platform discovery engine</h2>
          <p>Current audience totals and views from the latest available 28-day reporting window.</p>
        </div>
        {usingFallback && !loading && (
          <p className="media-kit-data-notice" role="status">
            Live statistics are temporarily unavailable. Displaying the latest saved media-kit baseline.
          </p>
        )}
        <div className="media-kit-stat-grid">
          <article><strong>{formatCompactNumber(summary.totalFollowers)}</strong><span>Total followers</span></article>
          <article><strong>{formatCompactNumber(summary.totalViews28d)}</strong><span>Views in 28 days</span></article>
          <article><strong>{summary.platformCount}</strong><span>Major platforms</span></article>
          <article><strong>{summary.latestUpdatedAt ? formatUpdatedDate(summary.latestUpdatedAt) : "Current"}</strong><span>Latest data refresh</span></article>
        </div>
      </section>

      <section className="media-kit-shell media-kit-panel" aria-labelledby="media-kit-fit-title">
        <div className="media-kit-section-heading">
          <p className="media-kit-kicker">Brand fit</p>
          <h2 id="media-kit-fit-title">{content.brandFitTitle}</h2>
          <p>{content.brandFitIntro}</p>
        </div>
        <div className="media-kit-fit-grid">
          {content.brandFitPoints.map((point) => (
            <article key={point.title}>
              <span aria-hidden="true">0{content.brandFitPoints.indexOf(point) + 1}</span>
              <h3>{point.title}</h3>
              <p>{point.description}</p>
            </article>
          ))}
        </div>
        {discoveryStats.length > 0 && (
          <div className="media-kit-discovery">
            <div>
              <p className="media-kit-kicker">{content.discoveryTitle}</p>
              <p>{content.discoveryIntro}</p>
            </div>
            {discoveryStats.map((stat) => (
              <article key={stat.platform}>
                <strong>{formatPercent(stat.discoveryPercent)}</strong>
                <span>{stat.platform === "youtube" ? "watch time" : "views"} from non-followers</span>
                <small>{PLATFORM_LABELS[stat.platform]}</small>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="media-kit-shell media-kit-panel" aria-labelledby="media-kit-platforms-title">
        <div className="media-kit-section-heading">
          <p className="media-kit-kicker">Global footprint</p>
          <h2 id="media-kit-platforms-title">Reach by platform</h2>
          <p>Follower counts and rolling views update independently, so a temporary provider issue never blanks the full media kit.</p>
        </div>
        <div className="media-kit-platform-grid">
          {orderedStats.map((stat) => {
            const { Icon, href } = platformMeta[stat.platform];
            return (
              <article className={`media-kit-platform-card ${stat.platform}`} key={stat.platform}>
                <div className="media-kit-platform-title">
                  <Icon aria-hidden="true" />
                  <h3><a href={href} target="_blank" rel="noopener noreferrer">{PLATFORM_LABELS[stat.platform]}</a></h3>
                </div>
                <strong>{formatCompactNumber(stat.followersCount)}</strong>
                <span>{stat.platform === "youtube" ? "subscribers" : "followers"}</span>
                <p><b>{formatCompactNumber(stat.views28d)}</b> views in 28 days</p>
                <div className="media-kit-view-bar" aria-label={`${PLATFORM_LABELS[stat.platform]} share of platform views`}>
                  <span style={{ width: `${Math.max(((stat.views28d || 0) / maxViews) * 100, 2)}%` }} />
                </div>
                <p>{content.platformDescriptions?.[stat.platform]}</p>
                <small>Updated {formatUpdatedDate(stat.capturedAt)}{stat.platform === "tiktok" ? " · manually" : ""}</small>
              </article>
            );
          })}
        </div>
      </section>

      <section className="media-kit-shell media-kit-panel" aria-labelledby="media-kit-audience-title">
        <div className="media-kit-section-heading">
          <p className="media-kit-kicker">Consumer reach</p>
          <h2 id="media-kit-audience-title">{content.audienceTitle}</h2>
          <p>{content.audienceIntro}</p>
          <p>{content.audienceDetail}</p>
        </div>
        <div className="media-kit-audience-grid">
          <article>
            <span>Gender</span>
            <strong>{formatPercent(summary.femalePercent, "%")}</strong>
            <h3>Female audience average</h3>
            <p>Average across platforms currently reporting gender data.</p>
          </article>
          <article>
            <span>Age</span>
            <strong>{formatPercent(summary.age1844Percent, "%")}</strong>
            <h3>Audience aged 18–44</h3>
            <p>Prime career and spending brackets, led by the 25–34 demographic.</p>
          </article>
          <article>
            <span>Geography</span>
            <strong>{formatUnitedStatesRange(summary.unitedStatesRange)}</strong>
            <h3>United States audience</h3>
            <p>Supported by {supportingGeography}.</p>
          </article>
        </div>
        <div className="media-kit-categories">
          <h3>Best-fit brand categories</h3>
          <ul>{content.categories.map((category) => <li key={category}>{category}</li>)}</ul>
        </div>
        <p className="media-kit-methodology">Demographic averages include only platforms with available data; Facebook age and gender are excluded.</p>
      </section>

      <section className="media-kit-shell media-kit-panel" aria-labelledby="media-kit-opportunities-title">
        <div className="media-kit-section-heading">
          <p className="media-kit-kicker">Campaign ideas</p>
          <h2 id="media-kit-opportunities-title">{content.opportunitiesTitle}</h2>
          <p>{content.opportunitiesIntro}</p>
        </div>
        <div className="media-kit-opportunity-grid">
          {content.opportunities.map((opportunity) => (
            <article key={opportunity.title}>
              <h3>{opportunity.title}</h3>
              <p>{opportunity.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="media-kit-shell media-kit-cta">
        <div><p className="media-kit-kicker">Business & inquiries</p><h2>{content.ctaTitle}</h2><p>{content.ctaText}</p></div>
        <a href={`mailto:${content.contactEmail}`}>{content.contactEmail}<FaArrowRight aria-hidden="true" /></a>
      </section>
    </main>
  );
}
